// Outreach API client.

import { outreachFetch, currentBusinessId } from "./business";

const BASE = "/api/outreach";

// What one NeverBounce check costs. Every screen that can spend credits shows the
// money before it spends it — the September 8-9 drain was invisible because nothing did.
export const CREDIT_USD = 0.008;
export function creditCost(n: number): string {
  return `${n.toLocaleString()} credit${n === 1 ? "" : "s"} ≈ USD $${(n * CREDIT_USD).toFixed(2)}`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await outreachFetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
    ...init,
  });
  if (res.status === 401) { window.location.href = "/login"; throw new Error("Unauthorized"); }
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error((e as { error?: string }).error || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export interface Contact {
  id: number;
  name: string;
  title?: string | null;
  company?: string | null;
  country?: string | null;
  type?: string | null;
  source?: string | null;
  profile_url?: string | null;
  email?: string | null;
  alt_emails?: string | null;
  phone?: string | null;
  email_source?: string | null;
  verification_status: "unchecked"|"valid"|"invalid"|"risky"|"catchall"|"disposable"|"unknown";
  verification_provider?: string | null;
  verified_at?: string | null;
  review_status: "pending"|"relevant"|"not_relevant"|"done";
  reviewed_by_email?: string | null;
  reviewed_at?: string | null;
  notes?: string | null;
  deleted_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactFilter {
  q?: string;
  country?: string;
  type?: string;
  source?: string;
  review_status?: string;
  verification_status?: string;
  title_contains?: string;
  has_email?: "0" | "1" | "";
  include_deleted?: boolean;
  page?: number;
  limit?: number;
  sort?: string;
  order?: "asc" | "desc";
}

function qsFromFilter(f: ContactFilter): string {
  const u = new URLSearchParams();
  if (f.q) u.set("q", f.q);
  if (f.country) u.set("country", f.country);
  if (f.type) u.set("type", f.type);
  if (f.source) u.set("source", f.source);
  if (f.review_status) u.set("review_status", f.review_status);
  if (f.verification_status) u.set("verification_status", f.verification_status);
  if (f.title_contains) u.set("title_contains", f.title_contains);
  if (f.has_email) u.set("has_email", f.has_email);
  if (f.include_deleted) u.set("include_deleted", "1");
  if (f.page) u.set("page", String(f.page));
  if (f.limit) u.set("limit", String(f.limit));
  if (f.sort) u.set("sort", f.sort);
  if (f.order) u.set("order", f.order);
  const s = u.toString();
  return s ? `?${s}` : "";
}

export const api = {
  stats:    () => request<{
    total: number; alive: number; deleted: number; with_email: number;
    verification: { valid: number; invalid: number; risky: number };
    review: { pending: number; relevant: number; not_relevant: number };
  }>("/stats"),
  filters:  () => request<{ countries: string[]; types: string[]; sources: string[] }>("/filters"),
  list:     (f: ContactFilter = {}) =>
    request<{ contacts: Contact[]; total: number; page: number; limit: number; pages: number }>(`/contacts${qsFromFilter(f)}`),
  create:   (body: Partial<Contact>) =>
    request<{ ok: true; contact: Contact }>("/contacts", { method: "POST", body: JSON.stringify(body) }),
  update:   (id: number, body: Partial<Contact>) =>
    request<{ ok: true }>(`/contacts/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  restore:  (id: number) =>
    request<{ ok: true; affected: number }>("/contacts/bulk", { method: "POST", body: JSON.stringify({ ids: [id], action: "restore" }) }),
  delete:   (id: number, hard = false) =>
    request<{ ok: true }>(`/contacts/${id}${hard ? "?hard=1" : ""}`, { method: "DELETE" }),
  bulk:     (body: { ids?: number[]; filter?: ContactFilter; action: string; value?: string }) =>
    request<{ ok: true; affected: number }>("/contacts/bulk", { method: "POST", body: JSON.stringify(body) }),
  import:   (rows: Array<Partial<Contact>>, source_filename: string | undefined, business_id: number) =>
    request<{ batch_id: number; new_count: number; updated_count: number; row_count: number }>(
      "/import",
      { method: "POST", body: JSON.stringify({ source_filename, rows, business_id }) },
    ),
  verify:   (body: { ids?: number[]; filter?: ContactFilter; force?: boolean; limit?: number }) =>
    request<{
      ok: true; checked: number; errors?: number;
      reused?: number;            // settled from a result already paid for
      skipped?: number;           // left alone (dead domain, no email, already checked)
      credits_used?: number;      // what this run actually cost
      contacts_touched?: number;  // rows given a status, billed or not
      exhausted?: boolean;        // NeverBounce reported the balance gone
      results: { id: number; status: string; result?: string; error?: string }[];
    }>(
      "/verify",
      { method: "POST", body: JSON.stringify(body) },
    ),
  // contactId ties the credit to a contact in the spend log. force=true is a
  // deliberate re-test: without it the server returns an answer already bought for
  // this address rather than paying for it twice.
  verifyOne: (email: string, contactId?: number, force?: boolean) =>
    request<{ ok: boolean; email: string; result: string; requestId?: string | null; raw?: unknown; error?: string; cached?: boolean; checkedAt?: string }>(
      "/verify-one",
      { method: "POST", body: JSON.stringify({ email, contact_id: contactId, force }) },
    ),
  verifyLog: (f: VerifyLogFilter = {}) => {
    const u = new URLSearchParams();
    if (f.q) u.set("q", f.q);
    if (f.user) u.set("user", f.user);
    if (f.source) u.set("source", f.source);
    if (f.result) u.set("result", f.result);
    if (f.billed) u.set("billed", "1");
    if (f.from) u.set("from", f.from);
    if (f.to) u.set("to", f.to);
    if (f.page) u.set("page", String(f.page));
    if (f.per) u.set("per", String(f.per));
    const s = u.toString();
    return request<{
      rows: VerifyLogRow[];
      by_user: { day: string; user_email: string | null; calls: number; credits: number }[];
      total: number; credits: number; usd: number; credit_usd: number;
      distinct_addresses: number; people: number;
      page: number; per: number; pages: number;
    }>(`/verify-log${s ? `?${s}` : ""}`);
  },
  logs: (f: LogFilter = {}) => {
    const u = new URLSearchParams();
    if (f.q) u.set("q", f.q);
    if (f.status) u.set("status", f.status);
    if (f.campaign_id) u.set("campaign_id", String(f.campaign_id));
    if (f.page) u.set("page", String(f.page));
    if (f.per) u.set("per", String(f.per));
    const s = u.toString();
    return request<{ rows: EmailLog[]; total: number; page: number; per: number; pages: number }>(
      `/logs${s ? `?${s}` : ""}`,
    );
  },
};

export interface VerifyLogFilter {
  q?: string;
  user?: string;
  source?: string;          // modal | bulk | candidates
  result?: string;
  billed?: boolean;         // only calls that actually cost a credit
  from?: string;            // YYYY-MM-DD
  to?: string;
  page?: number;
  per?: number;
}

export interface VerifyLogRow {
  id: number;
  created_at: string;
  user_email: string | null;
  contact_id: number | null;
  email: string;
  source: string;
  result: string | null;
  credits: number;
  request_id: string | null;
  error: string | null;
  contact_name: string | null;
  contact_company: string | null;
}

export interface LogFilter {
  q?: string;
  status?: string;
  campaign_id?: number;
  page?: number;
  per?: number;
}

export interface EmailLog {
  id: number;
  to_email: string | null;
  status: "scheduled" | "queued" | "sending" | "sent" | "delivered" | "bounced" | "complained" | "failed" | string;
  campaign_id: number | null;
  campaign_name: string | null;
  contact_id: number | null;
  contact_name: string | null;
  contact_company: string | null;
  scheduled_at: string | null;
  created_at: string;
  sent_at: string | null;
  provider_id: string | null;
  error: string | null;
  opened_at?: string | null;
  clicked_at?: string | null;
}
